import { useCallback, useEffect, useState } from 'react';
import { API } from '../../../../../api';
import { useProgramContext } from '../../../../../hooks/useProgramContext';
import type { LearningPillar, PillarIcon, ProgramTypeKey } from '../../Program/components/learning-lifecycle/types';
import type { MemberProgramCardData, MemberProgramStatus, MemberRoleKey } from './types';

// Live data for the Member Learning Framework Dashboard.
// Reads: GET /members/learning-framework/{orgId} — returns pillars + programs
// in a single call (BE pre-joins so the FE doesn't have to round-trip).

const getAuthHeaders = () => {
  try {
    const raw = localStorage.getItem('tokens');
    const tokens = raw ? JSON.parse(raw) : {};
    return { idtoken: tokens.id_token || '', authorization: tokens.access_token || '' };
  } catch {
    return { idtoken: '', authorization: '' };
  }
};

const toPillar = (p: any): LearningPillar => ({
  id: String(p.id),
  name: p.name ?? '',
  order: typeof p.order === 'number' ? p.order : 0,
  icon: (p.icon ?? null) as PillarIcon | null
});

// BE returns memberProgress.timeRemainingSeconds; the card renders minutes.
// BE returns dueBy as an ISO string; the card expects unix seconds.
const isoToUnix = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
};

const toMemberProgram = (p: any): MemberProgramCardData | null => {
  if (!p) return null;
  // Programs without a learningPillarId surface in a synthetic "Unassigned"
  // column (rendered first in the dashboard). Keep the id as '' so the
  // dashboard render can detect orphans via `learningPillarId === ''`.
  const learningPillarId = p.learningPillarId ? String(p.learningPillarId) : '';
  const roles: MemberRoleKey[] = Array.isArray(p.roles)
    ? p.roles.filter((r: string) => r === 'mentor' || r === 'mentee')
    : [];
  const progress = p.memberProgress ?? {};
  const contentCompleted = typeof progress.contentCompleted === 'number' ? progress.contentCompleted : 0;
  const totalContent = typeof progress.totalContent === 'number' ? progress.totalContent : 0;
  const goalProgress = totalContent > 0 ? Math.round((contentCompleted / totalContent) * 100) : 0;
  const timeRemainingSeconds = typeof progress.timeRemainingSeconds === 'number' ? progress.timeRemainingSeconds : 0;
  const stats = p.mentorshipStats ?? null;
  return {
    id: String(p.id),
    learningPillarId,
    name: p.name ?? '',
    programType: (p.programType ?? 'role-excellence') as ProgramTypeKey,
    // Member card requires non-null model; fall back to masterclass which
    // is the layout with the fewest role-specific affordances.
    model: p.model ?? 'masterclass',
    roles,
    status: (progress.status ?? 'not-started') as MemberProgramStatus,
    dueBy: isoToUnix(p.dueBy),
    // BE doesn't surface mcqScore in v1 — leave null and let the card
    // collapse the row. Future iteration of memberProgress will include it.
    mcqScore: null,
    timeRemainingMinutes: timeRemainingSeconds > 0 ? Math.round(timeRemainingSeconds / 60) : null,
    goalProgress,
    menteesEngaged: stats?.menteesEngaged,
    mentorsEngaged: stats?.mentorsEngaged,
    messagesSent: stats?.messagesSent,
    meetingsCompleted: stats?.meetingsCompleted,
    futureMeetings: stats?.futureMeetings,
    owner: p.owner
      ? { id: String(p.owner.id), name: p.owner.name ?? '', headshot: p.owner.headshot }
      : { id: '', name: '' },
    unresolvedLessons: typeof p.unresolvedLessons === 'number' ? p.unresolvedLessons : 0,
    locked: p.locked === true,
    displayOrder: typeof p.displayOrder === 'number' ? p.displayOrder : null,
    resumeLesson:
      p.resumeLesson?.moduleId && p.resumeLesson?.taskId
        ? {
            moduleId: String(p.resumeLesson.moduleId),
            taskId: String(p.resumeLesson.taskId),
            lessonName: p.resumeLesson.lessonName ?? ''
          }
        : null
  };
};

export interface UseMemberLearningFrameworkResult {
  pillars: LearningPillar[];
  programs: MemberProgramCardData[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useMemberLearningFramework = (): UseMemberLearningFrameworkResult => {
  const { orgId, location } = useProgramContext();
  const [pillars, setPillars] = useState<LearningPillar[]>([]);
  const [programs, setPrograms] = useState<MemberProgramCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFramework = useCallback(async () => {
    if (!orgId) return;
    const { idtoken, authorization } = getAuthHeaders();
    if (!idtoken || !authorization) {
      setError('Missing auth tokens');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await API.getMemberLearningFramework({ orgId, idtoken, authorization, location });
      const rawPillars: any[] = Array.isArray(res?.data?.pillars) ? res.data.pillars : [];
      const rawPrograms: any[] = Array.isArray(res?.data?.programs) ? res.data.programs : [];
      const mappedPillars: LearningPillar[] = rawPillars.map(toPillar);
      setPillars(mappedPillars.sort((a, b) => a.order - b.order));
      setPrograms(rawPrograms.map(toMemberProgram).filter((p): p is MemberProgramCardData => p !== null));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Your Dashboard could not be loaded. Refresh the page and try again.');
    } finally {
      setLoading(false);
    }
  }, [orgId, location]);

  useEffect(() => {
    fetchFramework();
  }, [fetchFramework]);

  return { pillars, programs, loading, error, refetch: fetchFramework };
};
